import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Modal,
  Grid,
  Typography,
  MenuItem,
} from "@mui/material";
import ModalHeader from "../../modals/ModalHeader"; 
import { postJsonData, get } from "../../network/ApiController"; 
import ApiEndpoints from "../../network/ApiEndPoints"; 
import { okSuccessToast, apiErrorToast } from "../../utils/ToastUtil"; 
import ModalFooter from "../../modals/ModalFooter"; 
import ViewModal from "../../modals/ViewModal";
import Spinner from "../../component/loading-screen/Spinner";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40%",
  bgcolor: "background.paper",
  boxShadow: 24,
  fontFamily: "Poppins",
  p: 2,
  height: "max-content",
  overflowY: "scroll",
};

const EditProject = ({ open, handleClose, projectData, refresh }) => {
  const [request, setRequest] = useState(false);
  const [priority, setPriority] = useState("Medium");
  const [updatedProject, setUpdatedProject] = useState({
    name: "",
    start_date: "",
    end_date: "",
    merchant: "", 
    budget: "",
    priority: "",
    category: "",
    project_image: null,
  });

  const [initialMerchant, setInitialMerchant] = useState(""); // To store the initial merchant value
  const [imagePreview, setImagePreview] = useState(null);
  const [establishments, setEstablishments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
 
  const refreshEstablishments = useCallback(async () => {
    setLoading(true);
    try {
      get(
        `${ApiEndpoints.GET_USERS_BIGG}?role=merchant`,
        "",
        setLoading,
        (res) => {
          setEstablishments(res.data.data || []);
        },
        (error) => {
          apiErrorToast(error);
        }
      );
    } catch (error) {
      apiErrorToast("Failed to fetch establishments");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (open && projectData) {
      setUpdatedProject({
        name: projectData?.name || "",
        start_date: projectData?.start_date || "",
        end_date: projectData?.end_date || "",
        merchant: projectData?.establishment || "",
        budget: projectData?.budget || "",
        priority: priority,
        category: projectData?.category || "",
        project_image: null,
      });
      setInitialMerchant(projectData?.establishment || ""); // Set initial merchant value
      setImagePreview(projectData?.project_image || null);
  
      // Fetch establishments when modal is opened
      if (establishments.length === 0) {
        refreshEstablishments();
      }
    }
  }, [open, projectData, establishments.length, refreshEstablishments]);
 
  

  const handleEstablishmentClick = async () => {
    if (establishments.length === 0) {
      refreshEstablishments();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdatedProject({
      ...updatedProject,
      [name]: value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImagePreview(URL.createObjectURL(file));
      setUploadedImage(file);
      setUpdatedProject({
        ...updatedProject,
        project_image: file,
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const data = new FormData();
    Object.keys(updatedProject).forEach((key) => {
      // If the user hasn't changed the establishment, don't send it in the payload
      if (key !== "project_image" && (key !== "merchant" || updatedProject[key] !== initialMerchant)) {
        data.append(key, updatedProject[key]);
      }
    });

    if (uploadedImage) {
      data.append("project_image", uploadedImage);
    } else if (
      updatedProject.project_image &&
      typeof updatedProject.project_image !== "string"
    ) {
      data.append("project_image", updatedProject.project_image);
    }

    const updateProjectUrl = `${ApiEndpoints.UPDATE_PROJECT}/${projectData.id}`;

    postJsonData(
      updateProjectUrl,
      data,
      setRequest,
      () => {
        okSuccessToast("Project updated successfully");
        handleClose();
        refresh();
      },
      (error) => {
        apiErrorToast(error);
      }
    );
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
      
        <ModalHeader
          title="Edit Project"
          handleClose={handleClose}
          subtitle="Edit the details of your project"
        />
        <Spinner loading={request} />
        <Box
          component="form"
          id="EditProject"
          onSubmit={handleSubmit}
          noValidate
          autoComplete="off"
        >
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <TextField
                label="Project Name"
                name="name"
                value={updatedProject?.name}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Start Date"
                name="start_date"
                type="date"
                value={updatedProject?.start_date}
                onChange={handleChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="End Date"
                name="end_date"
                type="date"
                value={updatedProject?.end_date}
                onChange={handleChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label="Establishment"
                name="merchant"
                value={updatedProject?.merchant}
                onChange={handleChange}
                onClick={handleEstablishmentClick}
                required
                sx={{width:"100%"}}
              >
                {loading ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : (
                  establishments?.map((establishment) => (
                    <MenuItem key={establishment.id} value={establishment.id}>
                      {establishment?.merchant_profile?.establishment}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Budget"
                name="budget"
                type="number"
                value={updatedProject?.budget}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label="Priority"
                name="priority"
                value={updatedProject?.priority}
                onChange={handleChange}
                fullWidth
                required
              >
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Low">Low</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Typography>Upload New Project Image</Typography>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ width: "100%", marginBottom: "10px" }}
              />

              {!uploadedImage && projectData?.project_image && (
                <Box sx={{ mb: 2 }}>
                  <ViewModal
                    row={projectData}
                    title="View Project Image"
                    fileKey="project_image"
                  />
                </Box>
              )}

              {uploadedImage && (
                <Box sx={{ mt: 1 }}>
                  <img
                    src={imagePreview}
                    alt="New Preview"
                    width="100"
                    height="100"
                  />
                </Box>
              )}
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <ModalFooter form="EditProject" request={request} btn="Update" />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditProject;
